import { AlertTriangle, ImageIcon, Library, Loader2, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { productService, type ProductNode } from "@/entities/product/api/productService";
import { ProductPickSheet } from "@/entities/product/ui/ProductPickSheet";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { cn } from "@/shared/lib/utils";

import { videoService, type VideoEntity } from "../../api/videoService";
import { deriveProductRefKey, isUncertain, setEntityRole } from "../../model/productEntities";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import {
  addProductFromLibrary,
  briefableProducts,
  hasSampleImage,
  libraryBackedEntities,
} from "../../model/productLibrary";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * T2A.6b/6c 上传产品图 + 确认单(Track 2 Phase 2A)。
 *
 * 用户传产品/角色图 → uploadFrame 落 UPLOADS 桶拿 key → classifyImages 过 VLM 产实体表 →
 * 本面板展示「产品=图X」映射,role 可改、可删(分类会错 → 不做黑箱,呼应「接口喂的值永远是死的」)。
 * 低置信/未判出的实体高亮待确认。改动实时写回 form.entities + 派生 form.productRefKey。
 *
 * ai 模式路由器据图背书产品实体给产品 hero 镜做 i2i(真产品进渲染),T2V/I2V/R2V 对用户不可见。
 */
export interface ProductImagePanelProps {
  entities: VideoEntity[];
  onChange: (entities: VideoEntity[], productRefKey: string) => void;
  /**
   * 紧凑形态(2026-08-06 重排 C3)—— 备料台③文本框底下的「素材」行。
   *
   * ★**只压外壳,不压能力**。重排前这是一整张卡:标题 +「从产品库选」+「上传产品图」+
   *   **三行说明**(比它的两个按钮还占版面),而绝大多数任务根本没传图 —— 空态在为一段
   *   只有传了图才用得上的核对指引付一整块版面。
   *   compact 下:零图 = 两颗 ⊕ 按钮;**传了图,下面那条实体核对条原样展开**
   *   (role 可改 / 可删 / 低置信琥珀 / 图从哪来的说明,一个都没少),
   *   核对指引也在那时才出现 —— 它本来就是"有图之后"的话。
   */
  compact?: boolean;
  /**
   * compact 触发行的前/后插槽(素材行的「素材」标签、选人物按钮、字数计数)。
   *
   * ★为什么用插槽而不是让 VideoMaterialRow 自己排:compact 下本组件渲染的是
   *   **触发按钮行 + 下方核对条**两段。若把它整块塞进外层 flex 行,有图时核对条会被
   *   当成一个 flex item,「人物」按钮就跟整块垂直居中 —— 有图/没图两种样子。
   *   插槽让触发按钮与人物按钮真正同处一行,核对条永远在行下方。
   *   (同 `StoryboardToolbar.productSlot` 的既有范式。)
   */
  leading?: ReactNode;
  trailing?: ReactNode;
  /**
   * VID-BRIEF-1:从产品库选中某品时把整个节点交上去(卖点 chips 要 node.sellingPoints)。
   * ★2026-08-19 起是 **toggle 语义**:已选的再点一次 = 去选(多选抽屉里同一颗按钮负责两件事)。
   */
  onPickProduct?: (node: ProductNode) => void;
  /** 已选中的产品 code —— 抽屉里据此高亮,用户不用靠记忆选第二个。 */
  pickedProductCodes?: string[];
  /** 1005:当前视频草稿 id(有草稿时)→ 识别图片那笔账挂到这条草稿上。 */
  draftId?: number | null;
  /** 1013 V6:挂载即弹出产品库抽屉(「从产品库开始」入口带 videoEntry:"product")。 */
  autoOpenLibrary?: boolean;
  /** 1014 V6:autoOpenLibrary 生效(抽屉已弹出)后回调一次,供页面收掉一次性标记。 */
  onLibraryAutoOpened?: () => void;
}

const ROLE_LABELS: Record<VideoEntity["role"], string> = {
  product: "产品",
  character: "人物",
  logo: "品牌标志",
  scene: "场景",
  other: "其它",
};

/** 合并后重排 id 为 E1..En(唯一、稳定;分类端每批各自从 E1 起,不重排会撞 id)。 */
function reindex(entities: VideoEntity[]): VideoEntity[] {
  return entities.map((e, i) => ({ ...e, id: `E${i + 1}` }));
}

export function ProductImagePanel({
  entities,
  onChange,
  compact,
  leading,
  trailing,
  onPickProduct,
  pickedProductCodes,
  draftId,
  autoOpenLibrary,
  onLibraryAutoOpened,
}: ProductImagePanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null = 还没拉过(点开才拉);[] = 拉过了但树上一张图都没有(要给真空态,不是灰按钮)
  const [libraryNodes, setLibraryNodes] = useState<ProductNode[] | null>(null);
  const [libraryBusy, setLibraryBusy] = useState(false);
  /** 产品库读取失败(0926 修复波 P11):与「真没有在售产品」分开,抽屉里给重试。 */
  const [libraryFailed, setLibraryFailed] = useState(false);
  /**
   * 正在 presign 的 key(在途)—— 只用于同一 render cycle 内**去重**,不重复发同一个请求。
   * ★F 批:不再永久拉黑失败的 key(旧 `triedKeys` 一失败就永不重签 → 瞬时抖动/越权恢复/换 key
   *   都签不回来)。请求完成(成败都)即从在途集移除,失败的 key 因此能在下次 effect 触发时重签。
   */
  const inFlightKeys = useRef<Set<string>>(new Set());

  // V6:子组件 ProductPickSheet 的挂载 effect 先跑(抽屉已开),再通知上层收标记。
  useEffect(() => {
    if (autoOpenLibrary) onLibraryAutoOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只在标记亮起时通知一次
  }, [autoOpenLibrary]);

  const emit = (next: VideoEntity[]) => onChange(next, deriveProductRefKey(next));

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    if (picked.some((f) => !/^image\/(png|jpeg|webp)$/.test(f.type))) {
      setError("图片仅支持 png/jpeg/webp");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 1) 逐张上传拿 key（UPLOADS 桶），顺手存 url 供缩略图。
      const uploaded = await Promise.all(
        picked.map(async (f) => {
          const { key, url } = await videoService.uploadFrame(f);
          return { key, url };
        })
      );
      setPreviewUrls((m) => {
        const next = { ...m };
        uploaded.forEach((u) => (next[u.key] = u.url));
        return next;
      });
      // 2) 过 VLM 分类新批次（只分类新图，不重付已分类的）。
      const { entities: classified } = await videoService.classifyImages(
        uploaded.map((u) => u.key),
        undefined,
        draftId
      );
      // 3) 合并到既有实体后重排 id，派生 productRefKey，写回。
      emit(reindex([...entities, ...classified]));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 「从产品库选」(波3-①/⑤):不上传也能让成片里出现真实产品。
   *
   * 只在用户点开时才拉产品树 —— 绝大多数人走的是上传路,没必要每次进创建页都多打一个请求。
   */
  const openLibrary = useCallback(async () => {
    if (libraryNodes !== null || libraryBusy) return;
    setLibraryBusy(true);
    setError(null);
    setLibraryFailed(false);
    try {
      setLibraryNodes(await productService.list());
    } catch {
      // ★不再落成 [](那会被抽屉画成「产品库暂无在售产品」,且 libraryNodes!==null 后再也不重拉)。
      //   保持 null + 标失败:抽屉显示「没读到 · 重试」,重试/重开都会再拉一次。
      setLibraryFailed(true);
    } finally {
      setLibraryBusy(false);
    }
  }, [libraryNodes, libraryBusy]);

  /**
   * 产品图要现签才能预览:落库存的是永久 key,URL 会过期。
   *
   * ★F 批 修4「选了有图的产品只显示 E2」的两处根因:
   *   (a) 旧代码写死前缀闸 `k.startsWith("product-images/")`。真实产品图 key 的前缀由后端决定,
   *       写死一个假设就会把库来源产品图挡在门外(签不出 → 回落显 {e.id}=E2)。改为**不假设前缀**:
   *       任何还没有 previewUrl、也没在签的 key 都尝试 presign。上传路(handleFiles)已同步把 url
   *       塞进 previewUrls,`!previewUrls[k]` 会把它们排掉,不受影响。
   *   (b) 旧代码一失败就把 key 永久拉黑,瞬时抖动 / 越权后恢复 / 换 key 都再也签不回来。改为只用
   *       `inFlightKeys` 做在途去重,完成即移除,失败可在下次 effect 触发时重签。
   * ★死循环护栏(codex 原 P1)保留:effect 依赖 previewUrls 又 setPreviewUrls —— 只有当这一轮**真签出了
   *   新图**才写 state;全失败 → ok=[] → 不写 → 依赖不变 → 不触发下一轮。
   */
  useEffect(() => {
    const wanted = [
      ...entities.map((e) => e.key),
      // 库列表里的图也要签:「从产品库选」的卡片在**选中之前**就该显缩略图,不然功能一半没做完。
      ...(libraryNodes ?? []).flatMap((n) => n.sampleImages ?? []),
    ];
    const missing = [
      ...new Set(
        wanted.filter((k): k is string => !!k && !previewUrls[k] && !inFlightKeys.current.has(k))
      ),
    ];
    if (missing.length === 0) return;
    missing.forEach((k) => inFlightKeys.current.add(k));
    let cancelled = false;
    void Promise.all(
      missing.map((k) =>
        productService
          .presignImage(k)
          .then((r) => (r?.url ? { key: k, url: r.url } : null))
          .catch(() => null)
      )
    ).then((signed) => {
      // 无论成败都清在途 —— 否则失败的 key 卡在在途集里再也不会重试。
      missing.forEach((k) => inFlightKeys.current.delete(k));
      if (cancelled) return;
      const ok = signed.filter((s): s is { key: string; url: string } => !!s);
      if (ok.length === 0) return; // 全失败 → 不写 state,不触发下一轮
      setPreviewUrls((m) => {
        const next = { ...m };
        ok.forEach((s) => (next[s.key] = s.url));
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [entities, libraryNodes, previewUrls]);

  const pickFromLibrary = (node: ProductNode) => {
    // VID-PICK-2:有图=图实体+卖点;无图=只带卖点(不造空 key 实体,图去产品树补)。
    if (hasSampleImage(node)) {
      emit(addProductFromLibrary(entities, node));
    }
    onPickProduct?.(node);
    // 抽屉自己关(单选路 ProductPickSheet.pick 里 setOpen(false))。
  };

  /**
   * 「从产品库选」= 与海报 / 长图 / 记者**同一个**共享抽屉(2026-08-19)。
   *
   * ★换掉了什么:这里原来是本文件自己那份 grid-cols-2/3 平铺网格。真机实测(阳光400通信)
   *   19 个在售产品全部打平占满整屏、无品类分组、无搜索、「标准版 一年」出现两次无法区分、
   *   19 行全标「只带卖点(没挂图)」、点一下就收起且再打开时零选中标记。
   *   海报线 2026-07-28 治过同一批毛病,产物就是 ProductPickSheet —— 它的头注当时就写着
   *   「视频那条也迟早要用」。
   * ★trigger 原样保留本面板两种形态各自的按钮长相:抽的是**选择动作**,不是按钮样式。
   * ★懒加载不变:onOpen 才拉产品树,不在页面挂载时就打 /tenant/products。
   */
  const librarySheet = (trigger: ReactNode) => (
    <ProductPickSheet
      products={briefableProducts(libraryNodes)}
      allNodes={libraryNodes ?? []}
      loading={libraryBusy}
      error={libraryFailed}
      onRetry={() => void openLibrary()}
      onOpen={() => void openLibrary()}
      // 多选:点了不关抽屉、已选的带高亮 —— 老板原话「是否可以…多选」。
      multi
      pickedCodes={pickedProductCodes ?? []}
      onToggle={(n) => pickFromLibrary(n)}
      onPick={(n) => n && pickFromLibrary(n)}
      trigger={trigger}
      autoOpen={autoOpenLibrary}
      /* ★这条标注是视频线特有的能力,换抽屉时差点弄丢:没挂图的品选了也不会让成片里出现
         真实产品图(只带卖点)。用户要在**选之前**就知道这件事。 */
      renderBadge={(n) =>
        hasSampleImage(n) ? null : (
          <span className="shrink-0 text-2xs text-muted-foreground">只带卖点(没挂图)</span>
        )
      }
    />
  );

  const changeRole = (id: string, role: VideoEntity["role"]) =>
    emit(setEntityRole(entities, id, role));

  const remove = (id: string) => emit(reindex(entities.filter((e) => e.id !== id)));

  const hasProduct = entities.some((e) => e.source === "image" && e.role === "product" && !!e.key);
  const fromLibrary = libraryBackedEntities(entities);
  // 2B 真人图 spike 未过 → 人物图只承诺「锁形象/风格」,不承诺露真人本尊(gpt-image-2 i2i 真人脸
  // 会重绘 ≠ 本人;真人照喂 I2V 可能撞人像审核)。传老板照发现不像更失望,故话术先收住。
  const hasCharacter = entities.some((e) => e.source === "image" && e.role === "character");

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      multiple
      accept={IMAGE_ACCEPT}
      className="hidden"
      onChange={(e) => {
        void handleFiles(e.target.files);
        e.target.value = "";
      }}
    />
  );

  /**
   * 两种形态**只有头部不同**,下面的产品库展开 / 实体核对条 / 错误行是同一份 ——
   * 复制一份出去就是第二处判据,而核对条正是本次重排最不能丢的那块能力。
   */
  const compactTrigger =
    "inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-border/70 px-3 py-1.5 text-[12.5px] text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50";

  const header = compact ? (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {leading}
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className={compactTrigger}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          产品图
        </button>
        {librarySheet(
          <button type="button" disabled={busy} className={compactTrigger}>
            {libraryBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Library className="h-3.5 w-3.5" />
            )}
            从产品库选
          </button>
        )}
        {trailing}
        {fileInput}
      </div>
      {/* 核对指引只在**有图之后**出现 —— 它本来就是"有图之后"的话(见 compact 头注)。 */}
      {entities.length > 0 && (
        <p className="text-2xs text-muted-foreground">
          系统自动识别了类别,识别可能出错 —— 核对下面每张是「产品 / 人物 / 品牌标志…」,不对就改。
        </p>
      )}
    </>
  ) : (
    <>
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">产品图（可选，让成片里出现真实产品）</Label>
        <div className="flex-1" />
        {librarySheet(
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={busy}>
            {libraryBusy ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Library className="mr-1 h-3.5 w-3.5" />
            )}
            从产品库选
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-1 h-3.5 w-3.5" />
          )}
          上传产品图
        </Button>
        {fileInput}
      </div>

      <p className="text-xs text-muted-foreground">
        传产品实拍图，系统自动识别类别；识别可能出错，请在下方核对「这是产品 / 人物 /
        品牌标志…」并可改。 产品图会用于产品特写镜的首帧，成片里出现的是你上传的真实产品。
      </p>
    </>
  );

  return (
    <div className={compact ? "space-y-2" : "rounded-lg border bg-card p-4 space-y-3"}>
      {header}

      {entities.length > 0 && (
        <div className="space-y-2">
          {entities.map((e) => {
            const uncertain = isUncertain(e);
            return (
              <div
                key={e.id}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-2",
                  uncertain && "border-amber-400/70 bg-amber-50/50"
                )}
              >
                {previewUrls[e.key ?? ""] ? (
                  <img
                    src={previewUrls[e.key ?? ""]}
                    alt={ROLE_LABELS[e.role]}
                    className="h-12 w-12 shrink-0 rounded object-cover"
                  />
                ) : (
                  // F 批:签不出来时别只显裸「E2」(用户会以为坏了)—— 给图片占位 icon + 产品名首字
                  // (拿不到名字退回角色名),整块带 title 供悬停看全名。
                  <div
                    title={e.name?.trim() || ROLE_LABELS[e.role]}
                    className="flex h-12 w-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded bg-muted px-0.5 text-muted-foreground"
                  >
                    <ImageIcon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                    <span className="max-w-full truncate text-[9px] leading-none">
                      {e.name?.trim().slice(0, 4) || ROLE_LABELS[e.role]}
                    </span>
                  </div>
                )}

                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">识别为</span>
                    <Select
                      value={e.role}
                      onValueChange={(v) => changeRole(e.id, v as VideoEntity["role"])}
                    >
                      <SelectTrigger className="h-7 w-28 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(ROLE_LABELS) as VideoEntity["role"][]).map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {uncertain && (
                      <Badge
                        variant="outline"
                        className="gap-1 border-amber-500 text-amber-700 text-2xs"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        待确认
                      </Badge>
                    )}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`移除${ROLE_LABELS[e.role]}图`}
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => remove(e.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}

          {/* 波3-②:说清**这张图哪来的**。两条来源都会走 i2i 做产品特写镜首帧,但用户需要知道
              「这次用的是本店产品库里那张」还是「我刚传的这张」—— 尤其是零输入路径下,他可能压根
              没做过传图这个动作。含糊一句「已识别到产品图」会让人以为是自己传的。 */}
          {hasProduct &&
            (fromLibrary.some((e) => e.auto) ? (
              /* 波3-T4:自动注入的必须说「自动」—— 用户没做过任何选图动作,写成「已用」会让他
                 以为是自己选的(概览不许比实际乐观)。移除入口就是上面实体行的删除按钮。 */
              <p className="text-xs text-emerald-700">
                ✓ 你的文案提到了「{fromLibrary.map((e) => e.name).join("、")}
                」，已自动使用本店产品图做产品特写镜首帧；不需要可在上方移除。
              </p>
            ) : fromLibrary.length > 0 ? (
              <p className="text-xs text-emerald-700">
                ✓ 已用本店产品图（{fromLibrary.map((e) => e.name).join("、")}
                ），产品特写镜会用它做首帧。
              </p>
            ) : (
              <p className="text-xs text-emerald-700">
                ✓ 已识别到你上传的产品图，产品特写镜会用它做首帧。
              </p>
            ))}
          {hasCharacter && (
            <p className="text-xs text-muted-foreground">
              人物图用于锁定整体形象与风格，成片不承诺呈现本人相貌。
            </p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
